import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Redirect, Route, Switch, useHistory } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';

////////////////////////////////////////////////////////////////////////////////////
// NOTE: This is the only appropriate place to import the $APP_THEME directly
// All other usage of theme should be injected via the ThemeProvider or the useTheme hook
////////////////////////////////////////////////////////////////////////////////////
// eslint-disable-next-line no-restricted-imports
import { $APP_THEME, APP_CONFIG, ROUTES } from '@sd/shared';

import { DevMenu, ErrorBoundary, LazyRoute, Loader, NotFoundScene, RouteWithParams } from '@sd/components';
import { Container } from 'react-bootstrap';
import { RecoilRoot } from 'recoil';
import { Authenticated } from './components/Authenticated';
import { AutomaticEventLogger } from './components/AutomaticEventLogger';
import { ConnectRecoilState } from './components/ConnectRecoilState';
import { DetectAccidentalFullPageRefreshes } from './components/DetectAccidentalFullPageRefreshes';
import { DeveloperScene } from './components/DeveloperScene';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { KeepSessionAlive } from './components/KeepSessionAlive';
import { LogoutScene } from './components/LogoutScene';
import { OAuthCallbackScene } from './components/OAuthCallbackScene';
import { RaiseRecoilErrors } from './components/RaiseRecoilErrors';
import { ScrollToTop } from './components/ScrollToTop';
import { VersionChecker } from './components/VersionChecker';

////////////////////////////////////////////////////////////////////////////////////
// NOTE: When you add a new Top-Level UI Package like Add Vehicle, Listing Manager, etc.
// The package must be lazy loaded as seen below.
////////////////////////////////////////////////////////////////////////////////////
const AdminContainer = lazy(async () => ({
  default: (await import('@sd/admin')).AdminContainer
}));

const AddVehicleContainer = lazy(async () => ({
  default: (await import('@sd/add-vehicle')).AddVehicleContainer
}));

const StatisticsContainer = lazy(async () => ({
  default: (await import('@sd/statistics')).StatisticsContainer
}));

const ListingManagerContainer = lazy(async () => ({
  default: (await import('@sd/listing-manager')).ListingManagerContainer
}));

const AuctionRuns = lazy(async () => ({
  default: (await import('@sd/view-runs')).AuctionRuns
}));

const AuctionSales = lazy(async () => ({
  default: (await import('@sd/auction-sales')).AuctionSales
}));

const RunlistBlock = lazy(async () => ({
  default: (await import('@sd/runlist-block')).RunlistBlock
}));

const CSVUpload = lazy(async () => ({
  default: (await import('@sd/csv-upload')).CsvUpload
}));

export function App() {
  useEffect(showBuildInfo, []);

  const isUatOrProd = ['prod', 'uat'].includes(APP_CONFIG.build.environment);

  return (
    <Router>
      <AutomaticEventLogger />
      <RecoilRoot>
        <ThemeProvider theme={$APP_THEME}>
          <ErrorBoundary>
            <DetectAccidentalFullPageRefreshes />
            <Loader fullScreen loading={false}>
              {/* This is used to fix an issue where the header was on top of modals */}
              <Header zIndex={1039} />
              <DevMenu style={{ position: 'absolute', right: 0 }} />
              <MainContainer fluid className="p-3">
                <InnerContainer className="my-2">
                  <VersionChecker />
                  <ScrollToTop />
                  <Suspense fallback={<Loader loading />}>
                    {/* This should always be the top so recoil state sync is prioritized! */}
                    <ConnectRecoilState />
                    <KeepSessionAlive />
                    <RaiseRecoilErrors />
                    <Switch>
                      <RouteWithParams path={ROUTES.appShell.oauthCallback.route} component={OAuthCallbackScene} />
                      <Route path={ROUTES.appShell.logout.route} component={LogoutScene} />
                      <Route path="/">
                        <Authenticated>
                          <Switch>
                            <LazyRoute
                              path={ROUTES.listingManager.listingManager.route}
                              component={ListingManagerContainer}
                            />
                            <LazyRoute path={ROUTES.auctionRuns.auctionRuns.route} component={AuctionRuns} />
                            <LazyRoute path={ROUTES.sales.base.route} component={AuctionSales} />
                            <LazyRoute path={ROUTES.sales.runlistBlock.route} component={RunlistBlock} />
                            <LazyRoute path={ROUTES.csvUpload.base.route} component={CSVUpload} />
                            <LazyRoute path={ROUTES.addVehicle.base.route} component={AddVehicleContainer} />
                            <LazyRoute
                              path={ROUTES.statistics.accountStatistics.route}
                              component={StatisticsContainer}
                            />
                            <LazyRoute path={ROUTES.admin.accountSelect.route} component={AdminContainer} />
                            {!isUatOrProd && (
                              <Route exact path={ROUTES.appShell.developer.route} component={DeveloperScene} />
                            )}
                            <Route
                              exact
                              path="/"
                              component={() => {
                                if (isUatOrProd) {
                                  window.location.replace(ROUTES.external.legacySellerDashboard.sales.externalUrlFor());
                                  return null;
                                } else {
                                  return <Redirect push to={ROUTES.appShell.developer.locationFor()} />;
                                }
                              }}
                            ></Route>
                            <NotFoundScene />
                          </Switch>
                        </Authenticated>
                      </Route>
                    </Switch>
                  </Suspense>
                </InnerContainer>
              </MainContainer>
              <Footer />
            </Loader>
          </ErrorBoundary>
        </ThemeProvider>
      </RecoilRoot>
    </Router>
  );
}

const showBuildInfo = () => {
  if (APP_CONFIG.build.environment !== 'test') {
    // eslint-disable-next-line no-console
    console.log(
      `
      | ____       _ _             ____            _     _                         _
      |/ ___|  ___| | | ___ _ __  |  _ \\  __ _ ___| |__ | |__   ___   __ _ _ __ __| |
      |\\___ \\ / _ \\ | |/ _ \\ '__| | | | |/ _\` / __| '_ \\| '_ \\ / _ \\ / _\` | '__/ _\` |
      | ___) |  __/ | |  __/ |    | |_| | (_| \\__ \\ | | | |_) | (_) | (_| | | | (_| |
      ||____/ \\___|_|_|\\___|_|    |____/ \\__,_|___/_| |_|_.__/ \\___/ \\__,_|_|  \\__,_|
    `
        .split('\n')
        .map(line => line.trimStart().substr(1))
        .join('\n')
    );
    // eslint-disable-next-line no-console
    console.log(JSON.stringify(APP_CONFIG.build, null, 2));
  }
};

const InnerContainer = styled.div`
  min-height: 600px;
`;

function MainContainer(props: React.PropsWithChildren<any>) {
  const [currentPath, setCurrentPath] = useState<string>('');
  const [isCorrectPath, setIsCorrectPath] = useState<boolean>(false);
  const history = useHistory();
  useEffect(() => {
    const unlisten = history.listen(path => {
      setCurrentPath(path.pathname);
    });

    return unlisten;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const acceptedUrls = [ROUTES.auctionRuns.auctionRuns.locationFor().pathname];
    setIsCorrectPath(acceptedUrls.includes(currentPath ? currentPath : window.location.pathname));
  }, [currentPath]);
  return (
    <StyledContainer $isAccepted={isCorrectPath} fluid={props.fluid} className={props.className}>
      {props.children}
    </StyledContainer>
  );
}

const StyledContainer = styled(Container)<{ $isAccepted: boolean }>`
  width: min(${({ theme }) => theme.breakpoints.xxxl}px, 100%);

  ${prop => {
    return (
      prop.$isAccepted &&
      `
    @media (max-width: ${prop.theme.breakpoints.md}px) {
      min-width: 100%;
      background-color: #F6F6F6;
      padding: 1.2rem;
    } 
  `
    );
  }};
`;
